import React from "react";
import { Col, Row, Typography, Collapse, Divider, Alert } from "antd";
import { QuestionCircleOutlined, ExclamationCircleOutlined } from "@ant-design/icons";

const { Title, Paragraph, Text } = Typography;
const { Panel } = Collapse;

const PQQSubmission = () => {
  return (
    <Row type="flex">
      <Col span={20} offset={1}>
        <Title level={2}>PQQ Submission Guide</Title>
        <Paragraph style={{ fontSize: '16px', marginBottom: '24px' }}>
          This guide will walk you through the process of attaching your vendor profile to a 
          Pre-Qualification Questionnaire (PQQ), certifying your submission, and understanding 
          submission status and requirements.
        </Paragraph>

        <Alert
          message="Approver Permission Required"
          description="Only users with Approver permissions can attach, certify, and submit a PQQ. 
          If you don't have approver permissions, contact your account administrator."
          type="info"
          showIcon
          style={{ marginBottom: '24px' }}
        />

        <Divider />

        <Title level={4}>Frequently Asked Questions</Title>

        <Collapse defaultActiveKey={['1']} ghost>
          <Panel 
            header={
              <span>
                <QuestionCircleOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                What is the PQQ submission process?
              </span>
            } 
            key="1"
          >
            <Paragraph>
              The PQQ submission process involves three main steps:
            </Paragraph>
            <ol>
              <li>
                <Text strong>Complete Your Profile:</Text> Ensure your i4 Vendor Profile is 
                100% complete with all required information and documents.
              </li>
              <li>
                <Text strong>Make Payment:</Text> Submit the required payment ($1,250 for initial 
                PQQ) and wait for confirmation.
              </li>
              <li>
                <Text strong>Attach and Certify:</Text> Link your profile to a specific project 
                and bid ID, then certify and submit your PQQ.
              </li>
            </ol>
            <Paragraph>
              Once submitted, your PQQ will be reviewed by i4 Strategies, and you'll be notified 
              of the status.
            </Paragraph>
          </Panel>

          <Panel 
            header={
              <span>
                <QuestionCircleOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                Who can submit a PQQ?
              </span>
            } 
            key="2"
          >
            <Paragraph>
              <Text strong>Only users with Approver permissions can attach, certify, and submit 
              a PQQ.</Text> This is a security measure to ensure that only authorized personnel 
              can make official submissions on behalf of your company.
            </Paragraph>
            <Paragraph>
              If you need approver permissions:
            </Paragraph>
            <ul>
              <li>Contact your account administrator</li>
              <li>Or contact i4 Strategies support to request permission changes</li>
            </ul>
            <Paragraph>
              Regular users can complete the vendor profile and make payments, but cannot certify 
              or submit PQQs.
            </Paragraph>
          </Panel>

          <Panel 
            header={
              <span>
                <QuestionCircleOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                What does "certifying" a PQQ mean?
              </span>
            } 
            key="3"
          >
            <Paragraph>
              Certifying a PQQ means that you, as an authorized representative of your company, 
              are confirming that:
            </Paragraph>
            <ul>
              <li>All information in the PQQ is accurate, current, and complete</li>
              <li>You have the authority to make this certification on behalf of your company</li>
              <li>You understand that false or misleading information may result in disqualification</li>
              <li>You agree to notify i4 Strategies of any changes to the submitted information</li>
            </ul>
            <Paragraph>
              This certification is legally binding and represents your company's guarantee of 
              the information's accuracy.
            </Paragraph>
          </Panel>

          <Panel 
            header={
              <span>
                <QuestionCircleOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                How do I attach my profile to a PQQ?
              </span>
            } 
            key="4"
          >
            <Paragraph>
              To attach your profile to a PQQ:
            </Paragraph>
            <ol>
              <li>Navigate to the PQQ submission page (usually accessible from your Dashboard)</li>
              <li>Select or enter the project and bid ID you're submitting for</li>
              <li>Verify that your vendor profile is complete and up to date</li>
              <li>Click "Attach Profile" or similar button</li>
              <li>Review the attached information</li>
              <li>Proceed to certification</li>
            </ol>
            <Paragraph>
              <Text strong>Note:</Text> You can only attach your profile if it's 100% complete 
              and payment has been processed.
            </Paragraph>
          </Panel>

          <Panel 
            header={
              <span>
                <QuestionCircleOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                What information do I need to provide when submitting?
              </span>
            } 
            key="5"
          >
            <Paragraph>
              When submitting a PQQ, you'll need to provide:
            </Paragraph>
            <ul>
              <li><Text strong>Project Information:</Text> The specific project you're bidding on</li>
              <li><Text strong>Bid ID:</Text> The unique identifier for the bid</li>
              <li><Text strong>Certification:</Text> Check the certification checkbox confirming 
              information accuracy</li>
              <li><Text strong>Submitted By:</Text> Your name as the person certifying the submission</li>
            </ul>
            <Paragraph>
              All other information comes from your completed vendor profile, which is automatically 
              attached to the PQQ.
            </Paragraph>
          </Panel>

          <Panel 
            header={
              <span>
                <QuestionCircleOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                Why is the certification checkbox disabled?
              </span>
            } 
            key="6"
          >
            <Paragraph>
              The certification checkbox may be disabled for several reasons:
            </Paragraph>
            <ul>
              <li>
                <Text strong>Missing Approver Permission:</Text> Only users with approver permissions 
                can certify PQQs. Check your user role in your account settings.
              </li>
              <li>
                <Text strong>Incomplete Profile:</Text> Your vendor profile must be 100% complete 
                before you can certify.
              </li>
              <li>
                <Text strong>Payment Not Processed:</Text> Payment must be received and processed 
                before certification is allowed.
              </li>
              <li>
                <Text strong>Profile Not Attached:</Text> You must attach your profile to the PQQ 
                before certifying.
              </li>
            </ul>
            <Paragraph>
              If you believe you should have access but the checkbox is disabled, verify your 
              profile completion status and payment status, then contact support if issues persist.
            </Paragraph>
          </Panel>

          <Panel 
            header={
              <span>
                <QuestionCircleOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                What happens after I submit my PQQ?
              </span>
            } 
            key="7"
          >
            <Paragraph>
              After you submit your PQQ:
            </Paragraph>
            <ol>
              <li>
                <Text strong>Confirmation:</Text> You'll receive a confirmation that your PQQ 
                has been submitted successfully.
              </li>
              <li>
                <Text strong>Review Process:</Text> i4 Strategies will review your submission 
                and verify all information.
              </li>
              <li>
                <Text strong>Status Updates:</Text> You can check the status of your PQQ on your 
                Dashboard. Statuses may include:
                <ul>
                  <li><Text strong>Pending:</Text> Awaiting review</li>
                  <li><Text strong>Under Review:</Text> Currently being evaluated</li>
                  <li><Text strong>Approved:</Text> Pre-qualification approved</li>
                  <li><Text strong>Information Required:</Text> Additional information needed</li>
                  <li><Text strong>Rejected:</Text> Not approved (with reasons provided)</li>
                </ul>
              </li>
              <li>
                <Text strong>Notification:</Text> You'll be notified via email of any status 
                changes or requests for additional information.
              </li>
            </ol>
          </Panel>

          <Panel 
            header={
              <span>
                <QuestionCircleOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                Can I edit my PQQ after submission?
              </span>
            } 
            key="8"
          >
            <Paragraph>
              Once a PQQ is submitted, you cannot directly edit it. However:
            </Paragraph>
            <ul>
              <li>
                <Text strong>Update Your Profile:</Text> You can update your vendor profile at 
                any time. If significant changes occur, you should notify i4 Strategies within 
                7 days.
              </li>
              <li>
                <Text strong>Information Requests:</Text> If i4 Strategies requests additional 
                information or clarification, you can provide updates through the system.
              </li>
              <li>
                <Text strong>New Submissions:</Text> For new projects, you can create new PQQ 
                submissions using your updated profile.
              </li>
            </ul>
            <Paragraph>
              <Text strong>Important:</Text> If information in your submitted PQQ changes during 
              a contract term, you must notify i4 Strategies within 7 days of the change.
            </Paragraph>
          </Panel>

          <Panel 
            header={
              <span>
                <QuestionCircleOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                What if I need to submit multiple PQQs for different projects?
              </span>
            } 
            key="9"
          >
            <Paragraph>
              You can submit multiple PQQs for different projects using the same vendor profile. 
              The process is:
            </Paragraph>
            <ol>
              <li>Complete your vendor profile once (this serves as the base for all PQQs)</li>
              <li>For each project, create a new PQQ submission</li>
              <li>Attach your profile to each PQQ</li>
              <li>Certify and submit each PQQ separately</li>
            </ol>
            <Paragraph>
              <Text strong>Note:</Text> You only need to pay the initial $1,250 fee once. 
              However, if your profile needs significant updates between submissions, you may 
              need to ensure those updates are reflected.
            </Paragraph>
          </Panel>

          <Panel 
            header={
              <span>
                <QuestionCircleOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                What is a "Certification of No Change"?
              </span>
            } 
            key="10"
          >
            <Paragraph>
              A Certification of No Change is a document you submit with new bids confirming 
              that all information in your previously submitted PQQ remains accurate and current. 
              This is required when:
            </Paragraph>
            <ul>
              <li>Submitting bids for new projects after your initial PQQ submission</li>
              <li>Your company information has not changed since the last PQQ submission</li>
            </ul>
            <Paragraph>
              If any information has changed, you must notify i4 Strategies of those changes 
              instead of submitting a Certification of No Change.
            </Paragraph>
          </Panel>
        </Collapse>

        <Divider />

        <Title level={4}>Step-by-Step Submission Process</Title>
        <ol style={{ paddingLeft: '20px' }}>
          <li>
            <Text strong>Verify Profile Completion:</Text> Ensure your i4 Vendor Profile shows 
            100% completion on the Dashboard.
          </li>
          <li>
            <Text strong>Complete Payment:</Text> Submit the required $1,250 payment and wait 
            for confirmation (see Payment guide for details).
          </li>
          <li>
            <Text strong>Navigate to PQQ Submission:</Text> Go to the PQQ submission page from 
            your Dashboard or Applications list.
          </li>
          <li>
            <Text strong>Enter Project Information:</Text> Provide the project name and bid ID 
            you're submitting for.
          </li>
          <li>
            <Text strong>Attach Your Profile:</Text> Link your completed vendor profile to this 
            PQQ submission.
          </li>
          <li>
            <Text strong>Review Information:</Text> Carefully review all attached information to 
            ensure accuracy.
          </li>
          <li>
            <Text strong>Certify:</Text> Check the certification checkbox and enter your name in 
            the "Submitted by" field (approver permission required).
          </li>
          <li>
            <Text strong>Submit:</Text> Click the Submit button to finalize your PQQ submission.
          </li>
          <li>
            <Text strong>Confirmation:</Text> Wait for confirmation that your submission was 
            successful.
          </li>
          <li>
            <Text strong>Monitor Status:</Text> Check your Dashboard regularly for status updates 
            and any requests for additional information.
          </li>
        </ol>

        <Divider />

        <Title level={4}>Common Issues & Solutions</Title>
        
        <Alert
          message="Certification Checkbox Disabled"
          description={
            <div>
              <Paragraph style={{ marginBottom: '8px' }}>
                <ExclamationCircleOutlined style={{ marginRight: '8px' }} />
                If the certification checkbox is disabled, check:
              </Paragraph>
              <ul style={{ marginBottom: 0 }}>
                <li>Your user role has approver permissions</li>
                <li>Your profile is 100% complete</li>
                <li>Payment has been processed</li>
                <li>Your profile is attached to the PQQ</li>
              </ul>
            </div>
          }
          type="warning"
          showIcon
          style={{ marginBottom: '16px' }}
        />

        <Alert
          message="Submission Button Disabled"
          description="The submit button is disabled until all requirements are met: complete profile, 
          processed payment, attached profile, and certification checkbox checked."
          type="info"
          showIcon
          style={{ marginBottom: '16px' }}
        />

        <div style={{ marginTop: '32px', padding: '16px', backgroundColor: '#f0f2f5', borderRadius: '4px' }}>
          <Paragraph style={{ marginBottom: 0 }}>
            <Text strong>Need help with PQQ submission?</Text> Contact support at 
            <Text strong> info@i4strategies.com</Text> or <Text strong>212-909-9580</Text> for 
            assistance with the submission process or to resolve any issues.
          </Paragraph>
        </div>
      </Col>
    </Row>
  );
};

export default PQQSubmission;
